import { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChatCircleDots } from '@phosphor-icons/react';
import { ticketApi } from '../../api/ticket.api';
import { usePolling } from '../../hooks/useDraft';
import { useAuth } from '../../store/AuthContext';
import { isUser } from '../../utils/roleHelpers';
import { toPersian } from '../../utils/persianNumber';
import { previewTicketWords } from '../../utils/ticketTitle';
import { Btn } from '../ui/index';

const POLL_MS = 10000;

function ticketIdOf(row) {
  return row?.id ?? row?.ticket_id ?? null;
}

function ticketTitleOf(row) {
  return previewTicketWords(row?.ticket_title || row?.title || row?.response_title || '', 8)
    || 'تیکت بدون عنوان';
}

export default function ReplyUnreadListener() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const enabled = isUser(user?.type);
  const isManagerUser = user?.type === 'manager_user';
  const [items, setItems] = useState([]);
  const dismissedRef = useRef(new Set());

  const poll = useCallback(async () => {
    if (!enabled) return;
    try {
      const res = isManagerUser
        ? await ticketApi.getManagerUserUnseen({ _noRedirect: true })
        : await ticketApi.getUserUnseen({ _noRedirect: true });
      const list = Array.isArray(res.data) ? res.data : res.data?.tickets || [];
      setItems(list.filter((row) => ticketIdOf(row) != null));
    } catch {
      // keep last snapshot
    }
  }, [enabled, isManagerUser]);

  usePolling(poll, POLL_MS, enabled);

  const visible = useMemo(() => {
    const currentId = String(location.pathname.match(/^\/tickets\/(\d+)/)?.[1] || '');
    return items.filter((row) => {
      const id = String(ticketIdOf(row));
      if (!id || dismissedRef.current.has(id)) return false;
      if (currentId && id === currentId) return false;
      return true;
    });
  }, [items, location.pathname]);

  if (!enabled || !visible.length) return null;

  const latest = visible[visible.length - 1];
  const latestId = ticketIdOf(latest);
  const count = visible.length;

  const openLatest = () => {
    if (!latestId) return;
    navigate(`/tickets/${latestId}`);
  };

  const dismissLatest = (e) => {
    e.stopPropagation();
    if (latestId != null) dismissedRef.current.add(String(latestId));
    setItems((prev) => prev.filter((row) => String(ticketIdOf(row)) !== String(latestId)));
  };

  return (
    <div
      className="fade-in pj-reply-unread-box"
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed', bottom: 20, left: 20, zIndex: 1000,
        width: 300, background: '#fff', borderRadius: 14,
        border: '1px solid #233A7A', boxShadow: '0 10px 32px rgba(35, 58, 122, 0.18)',
        padding: '16px 18px', display: 'flex', gap: 12, alignItems: 'flex-start',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 40, height: 40, minWidth: 40, borderRadius: 12, background: '#eff6ff',
          color: '#3D8BE8', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <ChatCircleDots size={22} weight="fill" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#3D8BE8', marginBottom: 2 }}>
          {count > 1 ? `${toPersian(count)} پاسخ دیده‌نشده` : 'پاسخ جدید'}
        </div>
        <div style={{
          fontSize: 15, fontWeight: 700, color: '#3D8BE8', overflow: 'hidden',
          textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          پیام شما پاسخ داده شده
        </div>
        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {ticketTitleOf(latest)}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Btn variant="primary" size="sm" onClick={openLatest} style={{ minHeight: 44 }}>
            مشاهده پاسخ
          </Btn>
          <Btn variant="ghost" size="sm" onClick={dismissLatest} style={{ minHeight: 44 }}>
            بعداً
          </Btn>
        </div>
      </div>
      <button
        type="button"
        onClick={dismissLatest}
        aria-label="بستن اعلان پاسخ"
        style={{
          background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8',
          fontSize: 18, lineHeight: 1, padding: 0, minWidth: 44, minHeight: 44,
        }}
      >
        ✕
      </button>
    </div>
  );
}
